import { Avatar, DateFormatter, getAvatarColor, TreeNode } from '@components'

import {
  Flex,
  IconButton,
  InternalAccessor,
  ModalConfirmation,
  Space,
  Table,
  TableColumn,
  TableData,
  TableHoverActions,
  Text,
  Tooltip,
  useModal,
} from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Row } from 'react-table'

import { DocumentStaticActions, DocumentStaticActionType } from '@views/documents/elements/DocumentStaticActions'

import { ModalId } from '../../../../enums/modalId'
import { Scope } from '../../../../enums/scope'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { FileListSkeleton } from './elements/FileListSkeleton'
import { NameCell } from './elements/NameCell'
import * as Styled from './styles'
import { FileListItem, FileListItemType } from './types/fileListItem'
import { hasContextMenuActions } from './utils/hasContextMenuActions'

type FileListTableRow = TableData &
  FileListItem & {
    [InternalAccessor.StaticActions]?: boolean
    [InternalAccessor.HoverActions]?: boolean
    [InternalAccessor.Readonly]?: boolean
  }

interface DraggedItem {
  id: string
  sourceFolderId: string
  type: FileListItemType
}

interface FileListProps {
  draggedItem?: DraggedItem | null
  folderTree?: TreeNode[]
  hideAddedAt?: boolean
  isDragDisabled?: boolean
  items: FileListItem[]
  isLoading?: boolean
  onBulkDelete?: () => void
  onCheckedChange: (ids: string[]) => void
  onContextMenu?: (id: string, event: React.MouseEvent) => void
  onCopyLink?: (id: string) => void
  onDelete?: (id: string) => void
  onDownload?: (id: string) => void
  onFilesDropOnFolder?: (folderId: string, files: File[]) => void
  onItemDragEnd?: () => void
  onItemDragStart?: (item: FileListItem) => void
  onItemDrop?: (targetItem: FileListItem) => void
  onMove?: (id: string, type: FileListItemType) => void
  onRename?: (id: string, newName: string) => void
  onRowClick?: (item: FileListItem) => void
  restrictedActions?: DocumentStaticActionType[]
  selectedCount?: number
  showCheckboxes?: boolean
  showContextMenu?: boolean
  showDirectDeleteButton?: boolean
  showDirectDownloadButton?: boolean
}

export const FileList = ({
  draggedItem,
  folderTree,
  hideAddedAt = false,
  isDragDisabled = false,
  items,
  isLoading = false,
  onBulkDelete,
  onCheckedChange,
  onContextMenu: _onContextMenu,
  onCopyLink,
  onDelete,
  onDownload,
  onFilesDropOnFolder,
  onItemDragEnd,
  onItemDragStart,
  onItemDrop,
  onMove,
  onRename,
  onRowClick,
  restrictedActions,
  selectedCount = 0,
  showCheckboxes = true,
  showContextMenu = true,
  showDirectDeleteButton = false,
  showDirectDownloadButton = false,
}: FileListProps): ReactElement => {
  const { t } = useTranslation()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [dropTargetRowId, setDropTargetRowId] = useState<string | null>(null)
  const selectedIdsRef = useRef<string[]>([])

  const { open: openDeleteModal, close: closeDeleteModal } = useModal(ModalId.DocumentsFileListDeleteModal)

  const deleteModalContent = useMemo(() => {
    if (deletingId) {
      const item = items.find((i) => i.id === deletingId)
      if (item?.type === 'folder') {
        return {
          title: t('documents.file_list.delete_modal.folder.title'),
          message: t('documents.file_list.delete_modal.folder.message'),
        }
      }
      return {
        title: t('documents.file_list.delete_modal.document.title'),
        message: t('documents.file_list.delete_modal.document.message'),
      }
    }
    return {
      title: t('documents.file_list.delete_modal.bulk.title', { count: selectedCount }),
      message: t('documents.file_list.delete_modal.bulk.message'),
    }
  }, [deletingId, items, selectedCount, t])

  const handleRowSelect = useCallback(
    (row: Row<FileListTableRow>, isChecked: boolean) => {
      const prev = selectedIdsRef.current
      const next = isChecked ? [...prev, row.original.id] : prev.filter((id) => id !== row.original.id)
      selectedIdsRef.current = next
      onCheckedChange(next)
    },
    [onCheckedChange],
  )

  const handleAllRowsSelect = useCallback(
    (rows: Row<FileListTableRow>[], isChecked: boolean) => {
      const next = isChecked ? rows.map((r) => r.original.id) : []
      selectedIdsRef.current = next
      onCheckedChange(next)
    },
    [onCheckedChange],
  )

  const handleDeleteClose = useCallback(() => {
    setDeletingId(null)
    closeDeleteModal()
  }, [closeDeleteModal])

  const handleDeleteConfirm = useCallback(() => {
    if (deletingId && onDelete) {
      onDelete(deletingId)
    } else if (!deletingId && onBulkDelete) {
      onBulkDelete()
    }
    setDeletingId(null)
    closeDeleteModal()
  }, [deletingId, onDelete, onBulkDelete, closeDeleteModal])

  const handleRowClick = useCallback(
    (row: Row<FileListTableRow>): void => {
      onRowClick?.(row.original)
    },
    [onRowClick],
  )

  const canRenameItem = useCallback(
    (item: FileListItem) => item.type === 'document' || isAuthorized(Scope.DmsFolderWrite),
    [],
  )

  const isDescendantFolder = useCallback(
    (candidateId: string, ancestorId: string): boolean => {
      if (!folderTree) {
        return false
      }
      const nodesById = Object.fromEntries((folderTree ?? []).map((node) => [node.id, node]))
      let current = nodesById[candidateId]
      while (current?.parentId) {
        if (current.parentId === ancestorId) {
          return true
        }
        current = nodesById[current.parentId]
      }
      return false
    },
    [folderTree],
  )

  const findDropTargetItem = useCallback(
    (event: React.DragEvent<HTMLDivElement>): FileListItem | undefined => {
      const rowElement = (event.target as HTMLElement).closest<HTMLElement>('li[data-row-id]')
      const rowId = rowElement?.dataset.rowId
      const targetItem = rowId ? items.find((item) => item.id === rowId) : undefined

      if (targetItem?.type !== 'folder' || targetItem.isReadOnly) {
        return undefined
      }

      // For OS files, accept any valid folder without additional validation
      const isOsFileDrop = event.dataTransfer?.types.includes('Files')
      if (isOsFileDrop) {
        return isDragDisabled ? undefined : targetItem
      }

      // For internal drags, match validation rules from FolderTree.canDropOnNode()
      if (!draggedItem) {
        return undefined
      }

      // Cannot drop item onto itself
      if (draggedItem.id === targetItem.id) {
        return undefined
      }

      // Cannot drop item back into its source folder
      if (draggedItem.sourceFolderId === targetItem.id) {
        return undefined
      }

      // Folder moves require the tree to reject descendant targets.
      if (draggedItem.type === 'folder' && (!folderTree || folderTree.length === 0)) {
        return undefined
      }

      // Cannot drop folder into its own descendant
      if (draggedItem.type === 'folder' && isDescendantFolder(targetItem.id, draggedItem.id)) {
        return undefined
      }

      return targetItem
    },
    [draggedItem, folderTree, isDescendantFolder, isDragDisabled, items],
  )

  const handleTableDragOver = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      const targetItem = findDropTargetItem(event)

      if (targetItem) {
        event.preventDefault()
      }

      setDropTargetRowId(targetItem?.id ?? null)
    },
    [findDropTargetItem],
  )

  const handleTableDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget as Node | null
    if (!event.currentTarget.contains(nextTarget)) {
      setDropTargetRowId(null)
    }
  }, [])

  const handleItemDragEnd = useCallback(() => {
    setDropTargetRowId(null)
    onItemDragEnd?.()
  }, [onItemDragEnd])

  const handleTableDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      const targetItem = findDropTargetItem(event)

      if (targetItem) {
        event.preventDefault()

        const isOsFileDrop = event.dataTransfer.types.includes('Files')

        if (isOsFileDrop && !isDragDisabled) {
          // Let the event keep bubbling to the outer Dropzone so react-dropzone resets its own
          // isDragActive state; onFilesDropOnFolder marks the drop as handled so the outer
          // Dropzone's onFilesSelect (see useFolderContentDragAndDrop) skips re-uploading these
          // files into the currently displayed folder.
          onFilesDropOnFolder?.(targetItem.id, Array.from(event.dataTransfer.files))
        } else if (!isOsFileDrop) {
          onItemDrop?.(targetItem)
        }
      }

      setDropTargetRowId(null)
    },
    [findDropTargetItem, isDragDisabled, onFilesDropOnFolder, onItemDrop],
  )

  const tableData: FileListTableRow[] = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        [InternalAccessor.StaticActions]:
          showContextMenu &&
          !showDirectDeleteButton &&
          !showDirectDownloadButton &&
          hasContextMenuActions(item, restrictedActions),
        [InternalAccessor.HoverActions]: showDirectDeleteButton || showDirectDownloadButton,
        [InternalAccessor.Readonly]: item.isReadOnly,
      })),
    [items, restrictedActions, showContextMenu, showDirectDeleteButton, showDirectDownloadButton],
  )

  const columns = useMemo((): TableColumn<FileListTableRow>[] => {
    const tableColumns: TableColumn<FileListTableRow>[] = [
      {
        Header: t('documents.file_list.column.name'),
        accessor: 'name',
        Cell: ({ row, data }) => {
          const item = data[Number(row.id)] as FileListTableRow
          const handleNameCellStartEdit = canRenameItem(item) ? () => setEditingId(item.id) : undefined
          const handleNameCellDragStart = () => onItemDragStart?.(item)
          return (
            <NameCell
              isDraggable={
                !isDragDisabled &&
                !item.isReadOnly &&
                !!onItemDragStart &&
                (item.type === 'document' || isAuthorized(Scope.DmsFolderWrite))
              }
              isEditing={editingId === item.id}
              item={item}
              onDragEnd={handleItemDragEnd}
              onDragStart={handleNameCellDragStart}
              onRename={onRename}
              onStartEdit={handleNameCellStartEdit}
              onStopEdit={() => setEditingId(null)}
            />
          )
        },
        renderAsChildren: true,
      },
      {
        Header: hideAddedAt ? '' : t('documents.file_list.column.date_added'),
        accessor: 'addedAt',
        Cell: ({ row, data }) => {
          if (hideAddedAt) {
            return null
          }
          const { addedAt } = data[Number(row.id)] as FileListTableRow
          return <DateFormatter value={addedAt} />
        },
        renderAsChildren: true,
      },
      {
        Header: t('documents.file_list.column.added_by'),
        accessor: 'addedBy',
        Cell: ({ row, data }) => {
          const { addedBy } = data[Number(row.id)] as FileListTableRow
          return (
            <Flex alignItems="center">
              <Avatar noMargin size="m" name={addedBy} backgroundColor={getAvatarColor(0)} />
              <Space horizontal size="s8" />
              <Text variant="bodyMD">{addedBy}</Text>
            </Flex>
          )
        },
        renderAsChildren: true,
      },
    ]

    if (showDirectDeleteButton || showDirectDownloadButton) {
      return [
        ...tableColumns,
        {
          Header: '',
          accessor: InternalAccessor.HoverActions,
          Cell: ({ row, data }) => {
            const item = data[Number(row.id)] as FileListTableRow
            const isDocument = item.type === 'document'

            return (
              <TableHoverActions>
                {showDirectDownloadButton && isDocument && (
                  <TableHoverActions.Item
                    icon="download"
                    label={t('documents.actions.download')}
                    onClick={() => onDownload?.(item.id)}
                  />
                )}
                {showDirectDeleteButton && (
                  <Tooltip label={t('documents.actions.delete')} placement="bottom">
                    <IconButton
                      icon="trash"
                      size="sm"
                      variant="text"
                      onClick={() => {
                        setDeletingId(item.id)
                        openDeleteModal()
                      }}
                    />
                  </Tooltip>
                )}
              </TableHoverActions>
            )
          },
        },
      ]
    }

    if (showContextMenu) {
      return [
        ...tableColumns,
        {
          Header: '',
          accessor: InternalAccessor.StaticActions,
          Cell: ({ row, data }) => {
            const item = data[Number(row.id)] as FileListTableRow
            const isDocument = item.type === 'document'
            const getVisibleActions = () => {
              if (restrictedActions) {
                return restrictedActions
              }
              if (item.isReadOnly) {
                return [...(isDocument ? [DocumentStaticActionType.Download] : [])]
              }
              if (!canRenameItem(item)) {
                return []
              }
              return [
                DocumentStaticActionType.Rename,
                DocumentStaticActionType.Move,
                ...(onCopyLink ? [DocumentStaticActionType.CopyLink] : []),
                ...(isDocument ? [DocumentStaticActionType.Download] : []),
                DocumentStaticActionType.Delete,
              ]
            }
            const visibleActions = getVisibleActions()

            const handleActionSelect = (action: DocumentStaticActionType) => {
              switch (action) {
                case DocumentStaticActionType.Rename:
                  setEditingId(item.id)
                  break
                case DocumentStaticActionType.Move:
                  onMove?.(item.id, item.type)
                  break
                case DocumentStaticActionType.CopyLink:
                  onCopyLink?.(item.id)
                  break
                case DocumentStaticActionType.Download:
                  onDownload?.(item.id)
                  break
                case DocumentStaticActionType.Delete:
                  setDeletingId(item.id)
                  openDeleteModal()
                  break
              }
            }

            const handleWrapperClick = useCallback((e: React.MouseEvent) => {
              e.stopPropagation()
            }, [])

            return (
              <Styled.DocumentStaticActionsWrapper role="presentation" onClick={handleWrapperClick}>
                <DocumentStaticActions visibleActions={visibleActions} onSelect={handleActionSelect} />
              </Styled.DocumentStaticActionsWrapper>
            )
          },
        },
      ]
    }

    return tableColumns
  }, [
    canRenameItem,
    editingId,
    handleItemDragEnd,
    hideAddedAt,
    isDragDisabled,
    onCopyLink,
    onDownload,
    onItemDragStart,
    onMove,
    onRename,
    openDeleteModal,
    restrictedActions,
    showContextMenu,
    showDirectDeleteButton,
    showDirectDownloadButton,
    t,
  ])

  return (
    <Styled.TableWrapper
      dropTargetRowId={dropTargetRowId}
      onDragEnter={handleTableDragOver}
      onDragLeave={handleTableDragLeave}
      onDragOver={handleTableDragOver}
      onDrop={handleTableDrop}
    >
      {isLoading ? (
        <FileListSkeleton />
      ) : (
        <Table
          bordered={false}
          columns={columns}
          data={tableData}
          isSelectable={showCheckboxes}
          onAllRowsSelect={handleAllRowsSelect}
          onRowClick={handleRowClick}
          onRowSelect={handleRowSelect}
          rowSelectType="checkbox"
          withPagination={false}
          withTableSummary={false}
        />
      )}
      <ModalConfirmation
        danger
        id={ModalId.DocumentsFileListDeleteModal}
        okLabel={t('documents.file_list.delete_modal.delete_action')}
        cancelLabel={t('cancel')}
        title={deleteModalContent.title}
        message={deleteModalContent.message}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteClose}
      />
    </Styled.TableWrapper>
  )
}
